// Organization details are owned by the Google Sheet the lead came from. This
// client sends an edit to /api/lead-source, which re-resolves the row on the
// server, writes only mapped columns and returns what the sheet now holds.

import type { Lead } from '../../types/crm'
import { getProspect } from './unifiedLeadFields'

/** Facebook, Instagram and LinkedIn are Organizations columns, never Supabase links. */
export const SHEET_SOCIAL_FIELDS = ['facebook', 'instagram', 'linkedin'] as const
export type SheetSocialField = (typeof SHEET_SOCIAL_FIELDS)[number]

export type LeadSourceField = 'name' | 'contact' | 'website' | 'email' | 'phone' | 'city' | 'state' | SheetSocialField
export type LeadSourceValues = Partial<Record<LeadSourceField, string>>

export const LEAD_SOURCE_LABELS: Record<LeadSourceField, string> = {
  name: 'Business name',
  contact: 'Contact',
  website: 'Website',
  email: 'Email',
  phone: 'Phone',
  city: 'City',
  state: 'State',
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
}

export class LeadSourceError extends Error {
  constructor(message: string, readonly field: LeadSourceField | null) {
    super(message)
  }
}

/**
 * The fields this lead's source sheet has a real column for. The CRM workbook
 * owns a lead that has an Organizations/Opportunities row; otherwise only the
 * Prospecting columns that exist (Person, Email, Phone, Website) are editable.
 */
export function editableSourceFields(lead: Lead): LeadSourceField[] {
  const fields: LeadSourceField[] = []
  if (lead.organization) fields.push('name')
  if (lead.opportunity) fields.push('contact')
  if (lead.organization) fields.push('website', 'email', 'phone', 'city', 'state')
  if (fields.length) return fields
  return getProspect(lead) ? ['name', 'email', 'phone', 'website'] : []
}

/** Current values for the editable fields, as the sheet provided them. */
export function sourceValuesOf(lead: Lead): Record<LeadSourceField, string> {
  const prospect = !lead.organization && !lead.opportunity ? getProspect(lead) : null
  if (prospect) {
    return {
      name: prospect.person, contact: '', website: prospect.website, email: prospect.email, phone: prospect.phone,
      city: '', state: '', facebook: '', instagram: '', linkedin: '',
    }
  }
  const organization = lead.organization
  return {
    name: organization?.name ?? '',
    contact: lead.opportunity?.name ?? '',
    website: organization?.website ?? '',
    email: organization?.email ?? '',
    phone: organization?.phone ?? '',
    city: organization?.city ?? '',
    state: organization?.state ?? '',
    facebook: organization?.facebook ?? '',
    instagram: organization?.instagram ?? '',
    linkedin: organization?.linkedin ?? '',
  }
}

/** The request body: an identity snapshot of the loaded row plus the changes. */
export function buildLeadSourceRequest(lead: Lead, changes: LeadSourceValues) {
  const prospect = !lead.organization && !lead.opportunity ? getProspect(lead) : null
  if (prospect) {
    return {
      source: 'prospecting',
      prospect: {
        list: prospect.list,
        row: prospect.rowNumber,
        person: prospect.person,
        email: prospect.email,
        phone: prospect.phone,
        website: prospect.website,
        profile: prospect.profile,
      },
      changes,
    }
  }

  const { organization, opportunity } = lead
  return {
    source: 'crm',
    organization: organization
      ? { row: organization.sheetRow, name: organization.name, website: organization.website, email: organization.email, phone: organization.phone }
      : null,
    opportunity: opportunity
      ? { row: opportunity.sheetRow, name: opportunity.name, organization: opportunity.organization }
      : null,
    changes,
  }
}

/** The lead as the sheet now holds it, from the server's read-back values. */
export function applyLeadSourceValues<T extends Lead>(lead: T, values: LeadSourceValues): T {
  const prospect = !lead.organization && !lead.opportunity ? getProspect(lead) : null
  if (prospect) {
    const next = {
      ...prospect,
      ...(values.name !== undefined ? { person: values.name } : {}),
      ...(values.email !== undefined ? { email: values.email } : {}),
      ...(values.phone !== undefined ? { phone: values.phone } : {}),
      ...(values.website !== undefined ? { website: values.website } : {}),
    }
    return { ...lead, owner: next.person, prospect: next }
  }

  const organization = lead.organization
    ? {
        ...lead.organization,
        ...(['name', 'website', 'email', 'phone', 'city', 'state', ...SHEET_SOCIAL_FIELDS] as const).reduce<Partial<Record<LeadSourceField, string>>>(
          (patch, field) => (values[field] !== undefined ? { ...patch, [field]: values[field] } : patch),
          {},
        ),
      }
    : null
  const renamed = values.name !== undefined && lead.opportunity?.organization === lead.organization?.name
  const opportunity = lead.opportunity
    ? {
        ...lead.opportunity,
        ...(values.contact !== undefined ? { name: values.contact } : {}),
        ...(renamed ? { organization: values.name as string } : {}),
      }
    : null

  return {
    ...lead,
    company: organization?.name ?? lead.company,
    owner: opportunity?.name ?? lead.owner,
    organization,
    opportunity,
  }
}

export async function saveLeadSource(lead: Lead, changes: LeadSourceValues): Promise<LeadSourceValues> {
  const response = await fetch('/api/lead-source', {
    method: 'PUT',
    credentials: 'include',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify(buildLeadSourceRequest(lead, changes)),
  })

  let payload: { values?: LeadSourceValues; message?: string; field?: string } = {}
  try {
    payload = await response.json()
  } catch {
    // An empty or non-JSON body falls through to the generic message.
  }

  if (!response.ok) {
    const field = payload.field && payload.field in LEAD_SOURCE_LABELS ? (payload.field as LeadSourceField) : null
    throw new LeadSourceError(payload.message ?? 'The spreadsheet could not be updated. Please try again.', field)
  }

  return payload.values ?? {}
}
